import Module from '../../module';
import scroll from '../../../libs/scroll/scroll';
import ChartLib from 'chart.js/auto';

interface OptionsInterface {
	cssId: {
		heHubAboutHeChartChartGrowthCanvasId?: string;
		heHubAboutHeChartChartPricingCanvasId?: string;

		anim?: {
			heHubAboutHeChartContentSectionHeaderBigV2Cls: string;
			heHubAboutHeChartGrowthContentSectionHeaderCls: string;
			heHubAboutHeChartPricingContentSectionHeaderCls: string;
		}
	},

	slug: string
}

class Chart extends Module {

	publicVars: OptionsInterface;
	private chartGrowthInstance: ChartLib | null = null;
	private chartPricingInstance: ChartLib | null = null;

	constructor(options: OptionsInterface) {
		super('He Hub About He Chart Module');

		this.publicVars = options;
	}

	startup() {
		this.msg('startup');

		scroll.init();
		scroll.registerAnimatedElement(this.publicVars.cssId.anim?.heHubAboutHeChartContentSectionHeaderBigV2Cls || '', 'he_hub_about_he_chart', { animCls: 'animate__fadeInUp50P', once: true});
		scroll.registerAnimatedElement(this.publicVars.cssId.anim?.heHubAboutHeChartGrowthContentSectionHeaderCls || '', 'he_hub_about_he_chart', { animCls: 'animate__fadeInLeft25P', once: true});
		scroll.registerAnimatedElement(this.publicVars.cssId.anim?.heHubAboutHeChartPricingContentSectionHeaderCls || '', 'he_hub_about_he_chart', { animCls: 'animate__fadeInLeft25P', once: true});

		this.buildGrowthChart();
		this.buildPricingChart();
	}

	bind() {
		this.msg('bind');

		let ticking = false;

		window.addEventListener('resize', () => {
			if (!ticking) {
				window.requestAnimationFrame(() => {
					this.buildGrowthChart();
					this.buildPricingChart();
					ticking = false;
				});
			}
			ticking = true;
		})
	}

	buildPricingChart() {
		const data = [
			{ year: 2000, count: 85 },
			{ year: 2001, count: 85 },
			{ year: 2002, count: 85 },
			{ year: 2003, count: 85 },
			{ year: 2004, count: 100 },
			{ year: 2005, count: 100 },
			{ year: 2006, count: 100 },
			{ year: 2007, count: 100 },
			{ year: 2008, count: 600 },
			{ year: 2009, count: 1000 },
			{ year: 2010, count: 2000 },
			{ year: 2011, count: 500 },
			{ year: 2012, count: 600 },
			{ year: 2013, count: 700 },
			{ year: 2014, count: 800 },
			{ year: 2015, count: 900 },
			{ year: 2016, count: 1500 },
			{ year: 2017, count: 2000 },
			{ year: 2018, count: 2000 },
			{ year: 2019, count: 2000 },
			{ year: 2020, count: 2000 },
			{ year: 2021, count: 2000 },
			{ year: 2022, count: 2000 },
			{ year: 2023, count: 2000 },
			{ year: 2024, count: 2500 },
			{ year: 2025, count: 2500 },
		];

		const canvasElementHtml = document.querySelector(
			this.publicVars.cssId.heHubAboutHeChartChartPricingCanvasId!) as HTMLCanvasElement;

		if (!canvasElementHtml) return;

		if (this.chartPricingInstance) {
			this.chartPricingInstance.destroy();
		}

		const displayTitles = window.innerWidth > 600;

		let delayed: boolean;

		this.chartPricingInstance = new ChartLib(
			canvasElementHtml,
			{
				type: 'line',
				data: {
					labels: data.map(row => row.year),
					datasets: [
						{
							label: '³He USD/Litres: ',
							data: data.map(row => row.count),
							backgroundColor: '#107539', // Example: Pulsar green
							borderColor: '#107539',
							borderWidth: 3
						}
					]
				},
				options: {
					responsive: true,
					plugins: {
						legend: {
							display: false // This removes the color box and text at the top
						}
					},
					animation: {
						onComplete: () => {
							delayed = true;
						},
						delay: (context) => {
							let delay = 0;
							if (context.type === 'data' && context.mode === 'default' && !delayed) {
								delay = context.dataIndex * 100 + context.datasetIndex * 100;
							}
							return delay;
						},
					},
					scales: {
						x: {
							title: {
								display: displayTitles,
								text: 'Year',
								color: '#ffffff',
								font: {
									family: 'source-sans-pro',
									size: 20,
									weight: 500,
									lineHeight: 1.2,
								},
								padding: {top: 20 }
							},
							ticks: {
								color: 'rgba(255,255,255, 0.5)', // This makes the years white
								callback: function(val, index) {
									const year = this.getLabelForValue(val as number);

									// Only return the year if it is one of the target years
									if ([2000, 2005, 2010, 2015, 2020, 2025].includes(Number(year))) {
										return year;
									}
									return null; // Hide all other years
								},
							},
							grid: {
								display: true,
								drawOnChartArea: true,
								drawTicks: false,
								color: 'rgba(255, 255, 255, 0.1)',
								z: -1 // Explicitly draw grid lines behind bars
							}
						},
						y: {
							title: {
								display: displayTitles,
								text: '³He USD/Litres',
								color: '#ffffff',
								font: {
									family: 'source-sans-pro',
									size: 18,
									weight: 500,
									lineHeight: 1.2,
								},
								padding: { bottom: 20 }
							},
							border: {
								display: false,
								color: 'rgba(255, 255, 255, 0.2)'
							},
							grid: {
								display: true,
								drawOnChartArea: true,
								drawTicks: false,
								color: 'rgba(255, 255, 255, 0.1)',
								z: -1 // Explicitly draw grid lines behind bars
							},
							ticks: {
								color: 'rgba(255,255,255, 0.5)',
								stepSize: 500,
								callback: function(value) {
									// Formats the labels to show '5k', '10k', etc.
									if (value === 0) return value; // Keep 0 as is, or return '' to hide it
									return (value as number);
								}
							}
						},
					}

				}
			}
		);
	}

	buildGrowthChart() {
		const data = [
			{ year: 2025, count: 1200 },
			{ year: 2026, count: 1560 },
			{ year: 2027, count: 2028 },
			{ year: 2028, count: 2636 },
			{ year: 2029, count: 3427 },
			{ year: 2030, count: 4456 },
			{ year: 2031, count: 5792 },
			{ year: 2032, count: 7530 },
			{ year: 2033, count: 9789 },
			{ year: 2034, count: 12725 },
			{ year: 2035, count: 16543 },
		];

		const canvasElementHtml = document.querySelector(
			this.publicVars.cssId.heHubAboutHeChartChartGrowthCanvasId!) as HTMLCanvasElement;

		if (!canvasElementHtml) return;

		if (this.chartGrowthInstance) {
			this.chartGrowthInstance.destroy();
		}

		const displayTitles = window.innerWidth > 600;

		let delayed: boolean;

		this.chartGrowthInstance = new ChartLib(
			canvasElementHtml,
			{
				type: 'bar',
				data: {
					labels: data.map(row => row.year),
					datasets: [
						{
							label: '³He Litres: ',
							data: data.map(row => row.count),
							backgroundColor: '#107539', // Example: Pulsar green
							borderColor: '#107539',
							borderWidth: 1
						}
					]
				},
				options: {
					responsive: true,
					plugins: {
						legend: {
							display: false // This removes the color box and text at the top
						}
					},
					animation: {
						onComplete: () => {
							delayed = true;
						},
						delay: (context) => {
							let delay = 0;
							if (context.type === 'data' && context.mode === 'default' && !delayed) {
								delay = context.dataIndex * 100 + context.datasetIndex * 100;
							}
							return delay;
						},
					},
					scales: {
						x: {
							title: {
								display: displayTitles,
								text: 'Year',
								color: '#ffffff',
								font: {
									family: 'source-sans-pro',
									size: 20,
									weight: 500,
									lineHeight: 1.2,
								},
								padding: {top: 20 }
							},
							ticks: {
								color: 'rgba(255,255,255, 0.5)' // This makes the years white
							},
							grid: {
								display: false // Optional: hides vertical grid lines if desired
							}
						},
						y: {
							title: {
								display: displayTitles,
								text: '³He Litres',
								color: '#ffffff',
								font: {
									family: 'source-sans-pro',
									size: 18,
									weight: 500,
									lineHeight: 1.2,
								},
								padding: { bottom: 20 }
							},
							border: {
								display: false,
								color: 'rgba(255, 255, 255, 0.2)'
							},
							grid: {
								display: true,
								drawOnChartArea: true,
								drawTicks: false,
								color: 'rgba(255, 255, 255, 0.1)',
								z: -1 // Explicitly draw grid lines behind bars
							},
							ticks: {
								color: 'rgba(255,255,255, 0.5)',
								stepSize: 5000, // Ensures lines appear at 5000, 10000, 15000, 20000
								callback: function(value) {
									// Formats the labels to show '5k', '10k', etc.
									if (value === 0) return value; // Keep 0 as is, or return '' to hide it
									return (value as number) / 1000 + 'k';
								}
							}
						},
					}

				}
			}
		);
	}
}

export default Chart;